'use client';

import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Eye, EyeOff, Loader2, Mail, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

import { LoginFormData } from './SignUpLoginContent';

interface LoginFormProps {
  form: UseFormReturn<LoginFormData>;
  onForgotPassword: () => void;
}

export default function LoginForm({ form, onForgotPassword }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const { signIn } = useAuth();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = form;

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      await signIn(data.email, data.password);

      // Determine role — check user_profiles table first, then fall back to metadata
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();

      let role = user?.user_metadata?.role ?? user?.app_metadata?.role ?? 'client';

      // Check the authoritative role from user_profiles table
      try {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', user?.id)
          .single();
        if (profile?.role) {
          role = profile.role;
        }
      } catch {
        // Fall back to metadata role if profile query fails
      }

      toast.success('Welcome back! Redirecting...');
      setTimeout(() => {
        if (role === 'admin') {
          router.push('/admin-dashboard');
        } else {
          router.push('/client-dashboard');
        }
        router.refresh();
      }, 600);
    } catch (err: any) {
      setIsLoading(false);
      setError('email', { message: err?.message || 'Invalid email or password' });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Welcome back</h2>
        <p className="text-muted-foreground text-sm mt-1">Sign in to your Marketing Biz Solutions account</p>
      </div>

      {/* Social auth */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { id: 'google-btn', label: 'Google', logo: 'G' },
          { id: 'microsoft-btn', label: 'Microsoft', logo: 'M' },
        ].map((provider) => (
          <button
            key={provider.id}
            type="button"
            className="flex items-center justify-center gap-2 px-4 py-2.5 border border-border rounded-lg text-sm font-medium text-secondary-foreground hover:bg-muted transition-all duration-150 active:scale-95"
          >
            <span className="font-bold text-primary">{provider.logo}</span>
            {provider.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <hr className="flex-1 border-border" />
        <span className="text-xs text-muted-foreground">or sign in with email</span>
        <hr className="flex-1 border-border" />
      </div>

      {/* Email */}
      <div>
        <label htmlFor="login-email" className="block text-sm font-semibold text-foreground mb-1.5">
          Email Address
        </label>
        <div className="relative">
          <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            id="login-email"
            type="email"
            placeholder="you@company.com"
            className={`input-field pl-9 ${errors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
            })}
          />
        </div>
        {errors.email && (
          <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
            <span>⚠</span> {errors.email.message}
          </p>
        )}
      </div>

      {/* Password */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label htmlFor="login-password" className="block text-sm font-semibold text-foreground">
            Password
          </label>
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-xs text-primary hover:underline font-medium"
          >
            Forgot password?
          </button>
        </div>
        <div className="relative">
          <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            id="login-password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter your password"
            className={`input-field pl-9 pr-10 ${errors.password ? 'border-red-500 focus:ring-red-500' : ''}`}
            {...register('password', { required: 'Password is required' })}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.password && (
          <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
            <span>⚠</span> {errors.password.message}
          </p>
        )}
      </div>

      {/* Remember me */}
      <div className="flex items-center gap-2">
        <input
          id="login-remember"
          type="checkbox"
          className="w-4 h-4 rounded border-border accent-primary"
          {...register('remember')}
        />
        <label htmlFor="login-remember" className="text-sm text-muted-foreground">
          Keep me signed in for 30 days
        </label>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading}
        className="btn-primary w-full justify-center py-3 text-base"
      >
        {isLoading ? (
          <>
            <Loader2 size={18} className="animate-spin" />
            Signing in...
          </>
        ) : (
          'Sign In to Dashboard'
        )}
      </button>
    </form>
  );
}